import { useState } from 'react'
import { Box, Divider, Link, Stack, Tab, Tabs, Typography } from '@mui/material'
import { PageSeo } from './page-seo'

const LEGAL_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Legal · TerraPlanner',
  url: 'https://terra-planner.com/legal',
  description: 'TerraPlanner\'s Privacy Policy and Terms of Service. We collect only what is necessary, operate under PIPEDA, and you own your farm data.',
}

const Section = ({ heading, children }) => (
  <Box>
    <Typography variant="h6" fontWeight={600} gutterBottom>{heading}</Typography>
    {children}
  </Box>
)

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} id={`legal-tab-${index}`} aria-labelledby={`legal-tab-btn-${index}`}>
    {children}
  </Box>
)

const PrivacyPolicyContent = () => (
  <Stack spacing={4} divider={<Divider />}>
    <Section heading="1. What we collect">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        When you create an account we collect your name and email address. When you add a farm we
        store its geographic coordinates (latitude and longitude) so we can display it on a map and
        calculate local season estimates. We do not collect payment card details directly — any
        payment processing is handled by a third-party provider subject to their own privacy policy.
      </Typography>
    </Section>

    <Section heading="2. How location data is used">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        Farm coordinates are used to:
      </Typography>
      <Box component="ul" sx={{ mt: 1, pl: 2.5, color: 'text.secondary' }}>
        {[
          'Display your fields on an interactive map',
          'Draw plot and patch polygons on the field canvas',
          'Estimate your local frost dates and season windows using climate data',
          'Look up your nearest address via Mapbox geocoding',
          'Generate static map overview thumbnails via Google Maps',
        ].map((item) => (
          <Typography key={item} component="li" variant="body2" lineHeight={1.75}>{item}</Typography>
        ))}
      </Box>
      <Typography variant="body2" color="text.secondary" lineHeight={1.75} sx={{ mt: 1 }}>
        Location data is never used for advertising and is never sold or shared with third parties
        beyond the service providers listed above.
      </Typography>
    </Section>

    <Section heading="3. Climate data">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        Land surface temperature (LST) data is fetched from the Copernicus Climate Change Service
        (C3S) ERA5 climate reanalysis dataset at your farm's location. This data is used exclusively
        to estimate your season's start and end dates and frost risk windows. It is not linked to
        your personal identity. Contains modified Copernicus Climate Change Service information.
      </Typography>
    </Section>

    <Section heading="4. Demo accounts">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        Demo access via QR ticket generates a browser-level device fingerprint (screen resolution,
        language, timezone, hardware concurrency) to manage your access window. This fingerprint
        is stored on your device and in our database linked to the ticket only — not to your name,
        email, or any other personal data. Demo data is purged when the ticket expires.
      </Typography>
    </Section>

    <Section heading="5. Cookies and local storage">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        TerraPlanner uses browser local storage to cache your farm data and application preferences
        (such as colour theme) so the app loads quickly on repeat visits. We do not use third-party
        tracking cookies. Our analytics provider (Ahrefs) may set cookies to measure aggregate page
        traffic; no personally identifiable information is shared with them.
      </Typography>
    </Section>

    <Section heading="6. Data retention">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        Your account data, farm details, and crop plans are retained for as long as your account
        is active. Deleting your account removes all associated personal data from our systems.
        Aggregated, anonymised analytics data may be retained indefinitely.
      </Typography>
    </Section>

    <Section heading="7. Your rights">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        You have the right to access, correct, or delete any personal data we hold about you.
        You may request an export or deletion of your data at any time by contacting us at{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>.
        We will respond within 30 days.
      </Typography>
    </Section>

    <Section heading="8. Third-party services">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        TerraPlanner integrates the following third-party services, each subject to their own
        privacy policies:
      </Typography>
      <Box component="ul" sx={{ mt: 1, pl: 2.5, color: 'text.secondary' }}>
        {[
          { name: 'Supabase', url: 'https://supabase.com/privacy', desc: '— database and authentication' },
          { name: 'Mapbox', url: 'https://www.mapbox.com/legal/privacy', desc: '— map tiles and address search' },
          { name: 'Google Maps', url: 'https://policies.google.com/privacy', desc: '— static map imagery' },
          { name: 'Copernicus CDS', url: 'https://cds.climate.copernicus.eu', desc: '— climate reanalysis data' },
          { name: 'Ahrefs Analytics', url: 'https://ahrefs.com/privacy', desc: '— aggregate web traffic analytics' },
        ].map(({ name, url, desc }) => (
          <Typography key={name} component="li" variant="body2" lineHeight={1.75}>
            <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover">{name}</Link>
            {' '}{desc}
          </Typography>
        ))}
      </Box>
    </Section>

    <Section heading="9. Changes to this policy">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        We may update this policy as the product evolves. Material changes will be noted with an
        updated date at the top of this page. Continued use of TerraPlanner after a change
        constitutes acceptance of the revised policy.
      </Typography>
    </Section>

    <Section heading="10. Consent">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        By creating an account, you consent to the collection and use of your name and email address
        as described in this policy. Farm coordinates are collected only when you actively add a farm
        — this action constitutes your consent for that data. You may withdraw consent for
        non-essential data collection (such as analytics) at any time by contacting us at{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>.
        Demo fingerprinting is device-local and consent is implicit in activating a demo ticket.
      </Typography>
    </Section>

    <Section heading="11. Safeguards">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        We use appropriate technical and organisational measures to protect your personal information
        against unauthorised access, disclosure, or destruction. Specifically: all data is stored in
        Supabase, which encrypts data at rest and transmits it over TLS. Row-level security policies
        in our database restrict access so that each user can only read and modify their own data.
        Passwords are handled by Supabase Auth using bcrypt hashing and are never stored in plaintext.
        No personal data is logged server-side beyond what Supabase's built-in audit logs capture.
      </Typography>
    </Section>

    <Section heading="12. Accountability">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        TerraPlanner operates under Canada's{' '}
        <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA). We are
        accountable for all personal information in our custody, including information transferred
        to third-party processors. Our Privacy Officer can be reached at{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>.
      </Typography>
    </Section>

    <Section heading="13. Accuracy">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        We make reasonable efforts to keep the personal information we hold accurate, complete, and
        up to date. You can update your name and email address at any time via your account settings.
        Farm coordinates are user-supplied and can be updated or deleted through the app. We do not
        supplement user-provided data with information from external sources beyond geocoding your
        farm coordinates for display purposes.
      </Typography>
    </Section>

    <Section heading="14. Challenging compliance">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        You may challenge TerraPlanner's compliance with PIPEDA or this privacy policy by contacting
        our Privacy Officer at{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>.
        We will investigate all complaints and respond within 30 days. If a complaint is found to be
        justified we will take appropriate corrective measures. Unresolved complaints may be escalated
        to the Office of the Privacy Commissioner of Canada (OPC) at{' '}
        <Link href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" underline="hover">
          www.priv.gc.ca
        </Link>.
      </Typography>
    </Section>
  </Stack>
)

const TermsOfServiceContent = () => (
  <Stack spacing={4} divider={<Divider />}>
    <Section heading="1. Acceptance of terms">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        By accessing or using TerraPlanner (the "Service"), you agree to be bound by these Terms of
        Service. If you do not agree to these terms, do not use the Service. These terms apply to all
        users, including visitors, registered users, and anyone who accesses or uses the Service.
      </Typography>
    </Section>

    <Section heading="2. Description of service">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        TerraPlanner is a cloud-based farm planning application that helps growers manage fields,
        crop schedules, and seasonal planning. The Service is available on a free tier and through
        paid subscription plans. Features, storage limits, and availability may change over time.
      </Typography>
    </Section>

    <Section heading="3. Account registration and security">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        You must provide accurate information when creating an account. You are responsible for
        maintaining the confidentiality of your login credentials and for all activity under your
        account. Notify us immediately at{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>{' '}
        if you suspect unauthorised access to your account.
      </Typography>
    </Section>

    <Section heading="4. Free tier">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        The free tier provides access to core features subject to storage and usage limits as
        described in the application. Free tier limits may be adjusted with reasonable notice.
        We reserve the right to remove inactive free accounts after an extended period of inactivity.
      </Typography>
    </Section>

    <Section heading="5. Paid subscriptions">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        TerraPlanner offers optional paid support plans including Seedling Supporter (one-time
        storage credit) and Perennial Supporter (recurring monthly subscription). Payments are
        processed by a third-party payment provider. Subscription fees are charged in advance on a
        monthly basis. Consumed storage credits are non-refundable. You may cancel a recurring
        subscription at any time; cancellation takes effect at the end of the current billing period.
      </Typography>
    </Section>

    <Section heading="6. Data ownership">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        You retain full ownership of all farm data, crop plans, and other content you create in the
        Service. You grant TerraPlanner a limited, non-exclusive licence to store and process your
        data solely for the purpose of providing the Service. We do not claim ownership of your data
        and will not use it for any purpose beyond operating and improving the Service.
      </Typography>
    </Section>

    <Section heading="7. Acceptable use">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        You agree not to: (a) use the Service for any unlawful purpose; (b) attempt to gain
        unauthorised access to any part of the Service or its infrastructure; (c) scrape, crawl, or
        systematically extract data from the Service; (d) resell or sublicence access to the Service;
        or (e) interfere with the Service's availability or performance.
      </Typography>
    </Section>

    <Section heading="8. Intellectual property">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        The TerraPlanner name, logo, interface design, and source code are proprietary. All rights
        not expressly granted are reserved. Open-source dependencies used in the Service are governed
        by their respective licences — see our{' '}
        <Link href="/attributions" underline="hover">Attributions</Link>{' '}
        page for a full list.
      </Typography>
    </Section>

    <Section heading="9. Disclaimer of warranties">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        The Service is provided "as is" without warranties of any kind, express or implied. Crop
        schedules, frost date estimates, and season windows generated by the Service are informational
        only and do not constitute agronomic, horticultural, or professional farming advice. Results
        may vary depending on local conditions. You rely on this information at your own discretion.
      </Typography>
    </Section>

    <Section heading="10. Limitation of liability">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        To the maximum extent permitted by applicable law, TerraPlanner and its operators shall not
        be liable for any indirect, incidental, special, or consequential damages arising from your
        use of the Service, including but not limited to crop loss, business interruption, or loss of
        profit, even if we have been advised of the possibility of such damages. Our total aggregate
        liability shall not exceed the amount you paid us in the 12 months preceding the claim.
      </Typography>
    </Section>

    <Section heading="11. Termination">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        We may suspend or terminate your access to the Service if you breach these Terms, engage in
        activity that harms other users or the platform, or for any other reason at our discretion
        with reasonable notice where practicable. You may delete your account at any time through the
        account settings. Upon termination, your data will be deleted in accordance with our Privacy
        Policy.
      </Typography>
    </Section>

    <Section heading="12. Governing law">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        These Terms are governed by and construed in accordance with the laws of the Province of
        Ontario, Canada, and the federal laws of Canada applicable therein, without regard to
        conflict of law principles. Any disputes shall be subject to the exclusive jurisdiction of
        the courts of Ontario.
      </Typography>
    </Section>

    <Section heading="13. Changes to these terms">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        We may update these Terms from time to time. Material changes will be reflected by an updated
        date at the top of this page. Continued use of the Service after changes are posted
        constitutes your acceptance of the revised Terms. It is your responsibility to review these
        Terms periodically.
      </Typography>
    </Section>

    <Section heading="14. Contact">
      <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
        Questions about these Terms may be directed to{' '}
        <Link href="mailto:admin@terra-planner.com" underline="hover">admin@terra-planner.com</Link>.
      </Typography>
    </Section>
  </Stack>
)

export const Legal = () => {
  const [tab, setTab] = useState(0)

  return (
    <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 6, md: 8 }, maxWidth: 860, mx: 'auto' }}>
      <PageSeo
        title="Legal"
        description="TerraPlanner's Privacy Policy and Terms of Service. We collect only what is necessary, operate under PIPEDA, and you own your farm data."
        path="/legal"
        schema={LEGAL_SCHEMA}
      />

      <Typography component="h1" variant="h4" fontWeight={700} gutterBottom>
        Legal
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Last updated: March 2026
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mt: 3, mb: 4, borderBottom: 1, borderColor: 'divider' }}
        aria-label="legal tabs"
      >
        <Tab label="Privacy Policy" id="legal-tab-btn-0" aria-controls="legal-tab-0" />
        <Tab label="Terms of Service" id="legal-tab-btn-1" aria-controls="legal-tab-1" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <PrivacyPolicyContent />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <TermsOfServiceContent />
      </TabPanel>
    </Box>
  )
}
